/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useMemo} from 'react';
import {OfflineCanRequestStatus} from '@ion/core/src/types/services';
import QueryKey from '@ion/query/src/config/QueryKeys';
import useLoadOfflineCanRequests from '@ion/query/src/hooks/useLoadOfflineCanRequests';
import {
  useOfflineCanRequestsPaging,
  UseOfflineCanRequestsPagingResult
} from '../canrequests/useOfflineCanRequestsPaging';
import {
  getRemainingCanRequestCapacity,
  hasReachedPendingCanRequestLimit,
  MAX_PENDING_CAN_REQUESTS
} from './CanRequestUtils';

export interface UseVehicleCanRequestsDataOptions {
  esn: string;
  startDate?: string | null;
  endDate?: string | null;
  enabled: boolean;
}

export interface UseVehicleCanRequestsDataResult extends UseOfflineCanRequestsPagingResult {
  maxSelection: number;
  hasReachedPendingLimit: boolean;
}

/**
 * Loads a single vehicle ESN's CAN requests for the Vehicle Manager CAN
 * Requests tab: the paged grid rows, plus the ESN's PENDING requests, which
 * drive the create limit and the bulk selection cap.
 *
 * ```typescript
 * import {useVehicleCanRequestsData} from './useVehicleCanRequestsData';
 * ...
 * const {
 *   loadCanRequestRows,
 *   refresh,
 *   onRefresh,
 *   maxSelection,
 *   hasReachedPendingLimit
 * } = useVehicleCanRequestsData({
 *   esn       : '3272001351',
 *   startDate : null,
 *   endDate   : null,
 *   enabled   : true
 * });
 * ```
 *
 * @param   esn       The ESN whose CAN requests are loaded.
 * @param   startDate Optional `createdOn` lower bound.
 * @param   endDate   Optional `createdOn` upper bound.
 * @param   enabled   Determines whether CAN requests are loaded.
 * @returns The paged grid state, a refresh handler covering both queries,
 *          and the remaining PENDING capacity.
 */
export const useVehicleCanRequestsData = ({
  esn,
  startDate,
  endDate,
  enabled
}: UseVehicleCanRequestsDataOptions): UseVehicleCanRequestsDataResult => {
  const queryParams = useMemo(() => ({
    esn,
    'createdOn[gte]' : startDate || undefined,
    'createdOn[lte]' : endDate || undefined
  }), [esn, startDate, endDate]);

  const pendingParams = useMemo(() => ({
    esn,
    status : OfflineCanRequestStatus.Pending,
    pg     : 1,
    pgSize : MAX_PENDING_CAN_REQUESTS
  }), [esn]);

  const {onRefresh: onGridRefresh, ...gridPaging} = useOfflineCanRequestsPaging({
    queryParams,
    enabled : enabled && !!esn
  });

  const {
    offlineCanRequests: pendingCanRequests,
    refresh: onPendingRefresh
  } = useLoadOfflineCanRequests({
    params   : pendingParams,
    enabled,
    queryKey : QueryKey.PENDING_CAN_REQUESTS
  });

  const onRefresh = useCallback(() => {
    onGridRefresh();
    onPendingRefresh();
  }, [onGridRefresh, onPendingRefresh]);

  const maxSelection = useMemo(() => (
    getRemainingCanRequestCapacity(pendingCanRequests)
  ), [pendingCanRequests]);

  return {
    ...gridPaging,
    onRefresh,
    maxSelection,
    hasReachedPendingLimit : hasReachedPendingCanRequestLimit(pendingCanRequests)
  };
};

export default useVehicleCanRequestsData;
